import React, { FC, Fragment, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';

import { useCreateClientInvitationMutation, CreateClientInvitationInput, UserType } from 'api';
import { PaddedPaper } from 'components';
import { useNotify, useProtectedClient } from 'hooks';

import Actions from '../components/PeopleInvite/Actions';
import InviteForm from '../components/PeopleInvite/Form';
import { PeopleInviteSchema } from '../validations';

const useStyles = makeStyles(theme => ({
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
}));

const PeopleInvite: FC = () => {
  const classes = useStyles();
  const notify = useNotify();
  const client = useProtectedClient();
  const location = useLocation<{ emails: string[] }>();
  const { t } = useTranslation();
  const [working, setWorking] = useState(false);

  const initialValues: CreateClientInvitationInput = {
    uploadfile: null,
    email_addresses: location?.state?.emails || [],
    subject: null,
    message: null,
    email_from_address: getEmailToUse(),
    user_type: UserType.User,
  };

  const { mutate: createClientInvitation } = useCreateClientInvitationMutation({
    onError: handleError,
    onSuccess: handleCompleted,
  });

  function getEmailToUse() {
    const { email_sending_domain, email_from_address } = client;

    if (!email_sending_domain) {
      return null;
    }

    const emailToUse = email_from_address?.split('@')?.[0] ?? '';
    return `${emailToUse}@${email_sending_domain}`;
  }

  function handleError() {
    setWorking(false);
    notify.mutationError();
  }

  function handleCompleted() {
    setWorking(false);
    notify.info({ message: t('admin:emailsSent') });
  }

  function handleSubmit(values: CreateClientInvitationInput) {
    setWorking(true);
    return createClientInvitation({ data: values });
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={PeopleInviteSchema}
    >
      <Fragment>
        <PaddedPaper>
          <Grid container justify="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4">{t('admin:invitePeople')}</Typography>
            </Grid>
          </Grid>
          <Divider className={classes.divider} />
          <InviteForm />
        </PaddedPaper>
        <Actions working={working} />
      </Fragment>
    </Formik>
  );
};

export default PeopleInvite;
